import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface StoryLikeButtonProps {
  storyId: string;
  initialLikes?: number;
  className?: string;
  showText?: boolean;
}

export function StoryLikeButton({ storyId, initialLikes = 0, className = '', showText = false }: StoryLikeButtonProps) {
  const { user } = useAuth();
  const [likes, setLikes] = useState(initialLikes);
  const [isLiked, setIsLiked] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let subscription: ReturnType<typeof supabase.channel>;

    async function setupLikes() {
      if (!user) {
        if (mounted) {
          setLoading(false);
        }
        return;
      }

      try {
        // Check if user has liked this story
        const { data: existingLike } = await supabase
          .from('story_likes')
          .select('*')
          .eq('story_id', storyId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (mounted) {
          setIsLiked(!!existingLike);
        }

        // Get current likes count
        const { count } = await supabase
          .from('story_likes')
          .select('*', { count: 'exact', head: true })
          .eq('story_id', storyId);

        if (mounted) {
          setLikes(count || 0);
        }

        // Subscribe to changes for this specific story
        subscription = supabase.channel(`story_likes:${storyId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'story_likes',
              filter: `story_id=eq.${storyId}`,
            },
            async (payload) => {
              console.log('StoryLikeButton: Like change detected:', payload);
              
              if (!mounted) return;

              // Update likes count immediately
              const { count: newCount } = await supabase
                .from('story_likes')
                .select('*', { count: 'exact', head: true })
                .eq('story_id', storyId);

              console.log('StoryLikeButton: New count:', newCount);
              setLikes(newCount || 0);

              // Update isLiked status if this change affects the current user
              if (user && payload.new?.user_id === user.id) {
                if (payload.eventType === 'INSERT') {
                  setIsLiked(true);
                }
              } else if (user && payload.old?.user_id === user.id) {
                if (payload.eventType === 'DELETE') {
                  setIsLiked(false);
                }
              }
            }
          )
          .subscribe((status) => {
            console.log('StoryLikeButton subscription status:', status);
          });

      } catch (error) {
        console.error('Error setting up likes:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    setupLikes();

    return () => {
      mounted = false;
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [storyId, user]);

  // Update likes when initialLikes prop changes
  useEffect(() => {
    setLikes(initialLikes);
  }, [initialLikes]);

  const handleLike = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user || loading) return;

    try {
      setLoading(true);

      if (isLiked) {
        // Unlike - optimistically update UI
        setIsLiked(false);
        setLikes(prev => Math.max(0, prev - 1));

        const { error } = await supabase
          .from('story_likes')
          .delete()
          .eq('story_id', storyId)
          .eq('user_id', user.id);

        if (error) {
          // Revert optimistic update on error
          setIsLiked(true);
          setLikes(prev => prev + 1);
          throw error;
        }
      } else {
        // Like - optimistically update UI
        setIsLiked(true);
        setLikes(prev => prev + 1);

        // Check if like already exists to prevent duplicates
        const { data: existingLike } = await supabase
          .from('story_likes')
          .select('*')
          .eq('story_id', storyId)
          .eq('user_id', user.id)
          .maybeSingle();

        if (!existingLike) {
          const { error } = await supabase
            .from('story_likes')
            .insert([{ story_id: storyId, user_id: user.id }]);

          if (error) {
            // Revert optimistic update on error
            setIsLiked(false);
            setLikes(prev => Math.max(0, prev - 1));
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleLike}
      disabled={!user || loading}
      className={`flex items-center space-x-2 text-sm ${
        isLiked ? 'text-primary' : 'text-light/60 hover:text-primary'
      } ${
        showText ? 'btn bg-dark/10' : ''
      } transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
      <span>{likes}</span>
      {showText && <span>Like</span>}
    </button>
  );
}